import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import {
  Building2, BadgeDollarSign, CreditCard, CircleDollarSign, ReceiptText,
  CheckCircle2, AlertTriangle, Clock3,
} from "lucide-react";
import { toast } from "sonner";
import { ForceDeleteModeCard } from "@/components/ForceDeleteModeCard";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  PieChart, Pie, Cell, Legend,
} from "recharts";

const brl = (v) => Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const monthLabel = (value) => {
  const [year, month] = String(value || "").split("-");
  if (!year || !month) return value || "";
  return new Date(Number(year), Number(month) - 1, 1)
    .toLocaleDateString("pt-BR", { month: "short", year: "2-digit" })
    .replace(".", "");
};
const tooltipStyle = { background: "#0F172A", border: "1px solid #334155", borderRadius: 8, color: "#F8FAFC" };
const STATUS_COLORS = {
  PAGO: "#10B981",
  PENDENTE: "#F59E0B",
  ATRASADO: "#EF4444",
  CANCELADO: "#64748B",
};

export default function PlatformDashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/platform/dashboard")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(apiErr(e)));
  }, []);

  if (!data) return <div className="text-slate-400">Carregando painel da plataforma...</div>;

  const paymentMonths = (data.pagamentos_mensais || []).map((row) => ({
    ...row,
    label: monthLabel(row.competencia),
  }));
  const paymentStatus = (data.pagamentos_status_mes || []).filter((row) => Number(row.valor || 0) > 0);

  const cards = [
    { label: "Empresas", value: data.empresas_total, sub: `${data.empresas_ativas} ativas`, icon: Building2 },
    { label: "Assinaturas ativas", value: data.assinaturas_ativas, sub: "Ativas + trial", icon: CheckCircle2 },
    { label: "MRR", value: brl(data.mrr), sub: "Receita mensal recorrente", icon: BadgeDollarSign },
    { label: "Recebido no mês", value: brl(data.recebido_mes), sub: `Por data de pagamento · ${data.competencia}`, icon: CircleDollarSign },
    { label: "Pagamentos pendentes", value: data.pagamentos_pendentes, sub: brl(data.valor_pendente), icon: ReceiptText },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-bold text-slate-50">Dashboard do SaaS</h1>
        <p className="text-sm text-slate-400">Visão comercial e administrativa do Peça360.</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
        {cards.map((card) => (
          <Card key={card.label} className="card-metal p-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-xs uppercase tracking-wide text-slate-500 font-mono">{card.label}</div>
                <div className="mt-2 text-2xl font-bold text-slate-50">{card.value}</div>
                <div className="mt-1 text-xs text-slate-400">{card.sub}</div>
              </div>
              <card.icon className="h-5 w-5 text-violet-300" />
            </div>
          </Card>
        ))}
      </div>

      <div className="grid gap-4 xl:grid-cols-2">
        <Card className={`p-5 border ${
          (data.empresas_em_atraso_total || 0) > 0
            ? "bg-rose-950/20 border-rose-800/60"
            : "card-metal border-emerald-800/40"
        }`}>
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              {(data.empresas_em_atraso_total || 0) > 0 ? (
                <AlertTriangle className="h-5 w-5 text-rose-400" />
              ) : (
                <CheckCircle2 className="h-5 w-5 text-emerald-400" />
              )}
              <div className="font-semibold text-slate-100">Atenção financeira</div>
            </div>
            <span className={`rounded-full px-2.5 py-1 text-xs font-mono ${
              (data.empresas_em_atraso_total || 0) > 0
                ? "bg-rose-950/70 text-rose-300"
                : "bg-emerald-950/60 text-emerald-300"
            }`}>
              {data.empresas_em_atraso_total || 0} em atraso
            </span>
          </div>

          {(data.empresas_em_atraso || []).length ? (
            <div className="mt-4 space-y-2">
              {data.empresas_em_atraso.map((row) => (
                <div key={row.tenant_id} className="rounded-lg border border-rose-900/50 bg-rose-950/30 px-3 py-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <div className="font-medium text-rose-100">{row.empresa}</div>
                      <div className="mt-0.5 text-xs text-rose-300/80">
                        Vencimento mais antigo {row.vencimento || "não informado"}
                        {row.quantidade > 1 ? ` · ${row.quantidade} cobranças` : ""}
                        {(row.competencias || []).length ? ` · Competência(s): ${row.competencias.join(", ")}` : ""}
                      </div>
                    </div>
                    <div className="font-mono font-semibold text-rose-300">{brl(row.valor_atrasado)}</div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="mt-4 rounded-lg border border-emerald-900/40 bg-emerald-950/20 px-3 py-4 text-sm text-emerald-300">
              Nenhuma empresa com cobrança vencida.
            </div>
          )}
        </Card>

        <Card className="card-metal p-5">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-emerald-400" />
            <div className="font-semibold text-slate-100">Empresas em dia</div>
          </div>
          <p className="mt-1 text-xs text-slate-500">Pagas ou com cobrança ainda dentro do vencimento.</p>

          {(data.empresas_em_dia || []).length ? (
            <div className="mt-4 space-y-2">
              {data.empresas_em_dia.map((row) => (
                <div key={row.tenant_id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-800 bg-slate-950/40 px-3 py-3">
                  <div>
                    <div className="font-medium text-slate-100">{row.empresa}</div>
                    <div className="mt-0.5 text-xs text-slate-500">
                      {row.situacao === "PAGO"
                        ? "Pagamento confirmado"
                        : `A vencer${row.proximo_vencimento ? ` em ${row.proximo_vencimento}` : ""}`}
                    </div>
                  </div>
                  <span className={row.situacao === "PAGO" ? "text-emerald-400 text-xs font-medium" : "text-amber-400 text-xs font-medium"}>
                    {row.situacao === "PAGO" ? "EM DIA" : "A VENCER"}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="mt-4 text-sm text-slate-500">Nenhuma empresa classificada como em dia nesta competência.</div>
          )}

          {(data.empresas_sem_cobranca || []).length > 0 && (
            <div className="mt-4 border-t border-slate-800 pt-3">
              <div className="flex items-center gap-2 text-xs font-medium text-slate-400">
                <Clock3 className="h-4 w-4" />
                Sem cobrança lançada neste mês
              </div>
              <div className="mt-2 flex flex-wrap gap-2">
                {data.empresas_sem_cobranca.map((row) => (
                  <span key={row.tenant_id} className="rounded-full border border-slate-800 bg-slate-950/50 px-2.5 py-1 text-xs text-slate-400">
                    {row.empresa}
                  </span>
                ))}
              </div>
            </div>
          )}
        </Card>
      </div>

      <div className="grid gap-4 xl:grid-cols-3">
        <Card className="card-metal p-5 xl:col-span-2">
          <div className="flex items-center gap-2 text-slate-100 font-semibold">
            <CreditCard className="h-5 w-5 text-violet-300" />
            Pagamentos · últimos 6 meses
          </div>
          <p className="mt-1 text-xs text-slate-500">Valores por competência: recebido, pendente e atrasado.</p>
          <div className="mt-4 h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={paymentMonths}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" vertical={false} />
                <XAxis dataKey="label" stroke="#64748B" fontSize={12} />
                <YAxis stroke="#64748B" fontSize={12} tickFormatter={(v) => `R$ ${Number(v).toLocaleString("pt-BR", { notation: "compact" })}`} />
                <Tooltip contentStyle={tooltipStyle} formatter={(value, name) => [brl(value), name]} />
                <Legend />
                <Bar dataKey="pago" name="Recebido" fill="#10B981" radius={[4, 4, 0, 0]} />
                <Bar dataKey="pendente" name="Pendente" fill="#F59E0B" radius={[4, 4, 0, 0]} />
                <Bar dataKey="atrasado" name="Atrasado" fill="#EF4444" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="card-metal p-5">
          <div className="font-semibold text-slate-100">Situação do mês atual</div>
          <p className="mt-1 text-xs text-slate-500">{data.competencia} · valores lançados</p>
          <div className="mt-4 h-[300px]">
            {paymentStatus.length ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={paymentStatus}
                    dataKey="valor"
                    nameKey="status"
                    cx="50%"
                    cy="46%"
                    outerRadius={88}
                    innerRadius={48}
                    paddingAngle={2}
                  >
                    {paymentStatus.map((row) => (
                      <Cell key={row.status} fill={STATUS_COLORS[row.status] || "#64748B"} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={tooltipStyle} formatter={(value, name) => [brl(value), name]} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-sm text-slate-500 text-center px-4">
                Nenhum pagamento lançado para esta competência.
              </div>
            )}
          </div>
        </Card>
      </div>

      <Card className="card-metal p-5">
        <div className="flex items-center gap-2 text-slate-100 font-semibold">
          <CreditCard className="h-5 w-5 text-violet-300" />
          Controle financeiro inicial
        </div>
        <p className="mt-2 text-sm text-slate-400">
          Nesta fase os pagamentos são lançados manualmente. A estrutura já fica pronta para integrar um gateway de cobrança futuramente.
        </p>
      </Card>

      <ForceDeleteModeCard />
    </div>
  );
}
